import React, { useState, useEffect, useRef } from "react";
import { 
  Button, 
  Table, 
  TableHead, 
  TableRow, 
  TableCell, 
  TableBody, 
  TextField, 
  Select, 
  MenuItem, 
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Snackbar,
  Grid,
  Paper,
  Box
} from "@mui/material";
import { truckService } from "../services/api";

/** API index returns { data: Truck[] }; keep parent state as a plain array (matches App.js fetchData). */
function trucksListFromResponse(response) {
  const body = response?.data;
  if (Array.isArray(body?.data)) return body.data;
  if (Array.isArray(body)) return body;
  return [];
}

export default function TruckManager({ trucks, setTrucks }) {
  const [form, setForm] = useState({ 
    truck_number: "", 
    capacity: "", 
    status: "active",
    model: "Default" 
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, truckId: null, truckNumber: '' });
  const [successMessage, setSuccessMessage] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editingTruckId, setEditingTruckId] = useState(null);
  const deleteButtonRef = useRef();

  // Add useEffect to log trucks data changes
  useEffect(() => {
    console.log('Current trucks data:', trucks);
  }, [trucks]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (error) {
      setError(null);
    }
  };

  const validateForm = () => {
    // Check if truck number is empty
    if (!form.truck_number.trim()) {
      setError("Truck number is required");
      return false;
    }

    // Check if truck number already exists (excluding current truck if editing)
    const existingTruck = trucks.find(
      truck => truck.truck_number.toLowerCase() === form.truck_number.toLowerCase() &&
      (!isEditing || truck.id !== editingTruckId)
    );
    
    if (existingTruck) {
      setError("A truck with this number already exists");
      return false;
    }

    // Check if capacity is a valid number
    if (form.capacity && isNaN(form.capacity)) {
      setError("Load capacity must be a valid number");
      return false;
    }

    return true;
  };

  const handleEditClick = (truck) => {
    setForm({
      truck_number: truck.truck_number,
      capacity: truck.capacity,
      status: truck.status,
      model: truck.model || "Default"
    });
    setIsEditing(true);
    setEditingTruckId(truck.id);
    setError(null);
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const truckData = {
        truck_number: form.truck_number.trim(),
        capacity: form.capacity ? parseInt(form.capacity) : null,
        status: form.status,
        model: form.model || "Default"
      };
      if (isEditing && editingTruckId) {
        // Update truck
        const response = await truckService.update(editingTruckId, truckData);
        // Refresh truck list
        const updatedTrucksResponse = await truckService.getAll();
        setTrucks(trucksListFromResponse(updatedTrucksResponse));
        setSuccessMessage('Truck updated successfully');
        // Dispatch event to notify other components (like AllTrips) to refresh
        window.dispatchEvent(new CustomEvent('truckUpdated', { detail: { truckId: editingTruckId } }));
      } else {
        // Add truck
        const response = await truckService.create(truckData);
        if (response.data) {
          // Refresh truck list to ensure consistency
          const updatedTrucksResponse = await truckService.getAll();
          setTrucks(trucksListFromResponse(updatedTrucksResponse));
          setSuccessMessage('Truck added successfully');
          // Dispatch event to notify other components (like AllTrips) to refresh
          window.dispatchEvent(new CustomEvent('truckUpdated', { detail: { truckId: response.data.id, action: 'created' } }));
        } else {
          throw new Error('No data received from server');
        }
      }
      setForm({ truck_number: "", capacity: "", status: "active", model: "Default" });
      setIsEditing(false);
      setEditingTruckId(null);
    } catch (err) {
      let errorMessage = 'Failed to save truck. ';
      if (err.response?.data?.message) {
        errorMessage += err.response.data.message;
      } else if (err.response?.data?.error) {
        errorMessage += err.response.data.error;
      } else {
        errorMessage += 'Please try again.';
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (truck) => {
    setDeleteDialog({
      open: true,
      truckId: truck.id,
      truckNumber: truck.truck_number
    });
  };

  const handleDeleteConfirm = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Deleting truck:', deleteDialog.truckId);
      
      const response = await truckService.delete(deleteDialog.truckId);
      console.log('Delete response:', response);
      
      if (response.status === 200) {
        // Fetch updated trucks list after successful deletion
        const updatedTrucksResponse = await truckService.getAll();
        setTrucks(trucksListFromResponse(updatedTrucksResponse));
        
        setSuccessMessage('Truck deleted successfully');
        // Dispatch event to notify other components (like AllTrips) to refresh
        window.dispatchEvent(new CustomEvent('truckUpdated', { detail: { truckId: deleteDialog.truckId, action: 'deleted' } }));
      }
      
      // Close the dialog
      setDeleteDialog({ open: false, truckId: null, truckNumber: '' });
    } catch (err) {
      console.error('Delete error:', err);
      console.error('Error response:', err.response?.data);
      console.error('Error status:', err.response?.status);
      
      let errorMessage = 'Failed to delete truck. ';
      if (err.response?.data?.message) {
        errorMessage += err.response.data.message;
      } else if (err.response?.data?.error) {
        errorMessage += err.response.data.error;
      } else {
        errorMessage += 'Please try again.';
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, truckId: null, truckNumber: '' });
    deleteButtonRef.current?.focus();
  };

  // Update refreshTrucks function
  const refreshTrucks = async () => {
    try {
      setLoading(true);
      const response = await truckService.getAll();
      const list = trucksListFromResponse(response);
      setTrucks(list);
      setSuccessMessage('Trucks list refreshed');
    } catch (err) {
      console.error('Error fetching trucks:', err);
      let errorMessage = 'Failed to fetch trucks. ';
      if (err.response?.data?.message) {
        errorMessage += err.response.data.message;
      } else if (err.response?.data?.error) {
        errorMessage += err.response.data.error;
      } else {
        errorMessage += 'Please try again.';
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Call refreshTrucks when component mounts
  useEffect(() => {
    refreshTrucks();
  }, []);

  const handleCloseSnackbar = () => {
    setSuccessMessage('');
  };

  return (
    <div>
      <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold', mb: 3 }}>
        Truck Management
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField 
              fullWidth
              label="Truck Number" 
              name="truck_number" 
              value={form.truck_number} 
              onChange={handleChange}
              disabled={loading}
              required
              error={!!error && error.includes("truck number")}
              helperText={error && error.includes("truck number") ? error : ""}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField 
              fullWidth
              label="Load Capacity (Ton)" 
              name="capacity" 
              value={form.capacity} 
              onChange={handleChange}
              disabled={loading}
              type="number"
              InputProps={{ inputProps: { min: 0 } }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Select
              fullWidth
              name="status"
              value={form.status}
              onChange={handleChange}
              disabled={loading}
              displayEmpty
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleSubmit}
              disabled={loading}
              sx={{ height: '56px' }}
            >
              {isEditing ? 'Update Truck' : 'Add Truck'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Truck Number</TableCell>
            <TableCell>Model</TableCell>
            <TableCell>Load Capacity</TableCell>
            <TableCell>Status</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(Array.isArray(trucks?.data) ? trucks.data : Array.isArray(trucks) ? trucks : []).map((truck) => (
            <TableRow key={truck.id}>
              <TableCell>{truck.truck_number}</TableCell>
              <TableCell>{truck.model}</TableCell>
              <TableCell>{truck.capacity} Ton</TableCell>
              <TableCell>{truck.status}</TableCell>
              <TableCell align="right">
                <Button
                  size="small"
                  color="primary"
                  onClick={() => handleEditClick(truck)}
                  disabled={loading}
                  sx={{ mr: 1 }}
                >
                  Edit
                </Button>
                <Button
                  size="small"
                  color="error"
                  onClick={() => handleDeleteClick(truck)}
                  disabled={loading}
                  ref={deleteButtonRef}
                >
                  Delete
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete truck {deleteDialog.truckNumber}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" disabled={loading}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Message Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        message={successMessage}
      />
    </div>
  );
} 